import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import templateService from "../../../services/template.service";
import Loading from "../../common/Loading";
import Swal from '../../helpers/swal';
import {
    DEFAULT_CALL_INTERACTION_TYPE,
    DEFAULT_CALL_LANGUAGE,
    DEFAULT_CALL_VOICE,
    optionRowsToMap,
    optionsMapToRows,
    serializeCallOptions,
} from './callTemplateOptions';
import {
    AdminCallTemplateFields,
    AdminCallTemplateFooter,
    AdminCallTemplateInteractionFields,
    AdminCallTemplateMetaGrid,
    AdminCallTemplatePage,
} from './AdminCallTemplateForm';

const EditAdminCallTemplate = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        reminderName: '',
        content: '',
        voice: DEFAULT_CALL_VOICE,
        language: DEFAULT_CALL_LANGUAGE,
        interactionType: DEFAULT_CALL_INTERACTION_TYPE,
        invalidPrompt: '',
        timeoutPrompt: '',
        usedBy: 'CLINIC',
        mediumType: 'CALL',
    });
    const [optionRows, setOptionRows] = useState([]);
    const [originalData, setOriginalData] = useState(null);
    const [originalOptionRows, setOriginalOptionRows] = useState([]);
    const [error, setError] = useState(null);

    const applyTemplateToForm = (templateData) => {
        const rows = optionsMapToRows(templateData.options);
        setFormData({
            reminderName: templateData.reminderName || '',
            content: templateData.content || '',
            voice: templateData.voice || DEFAULT_CALL_VOICE,
            language: templateData.language || DEFAULT_CALL_LANGUAGE,
            interactionType: templateData.interactionType || DEFAULT_CALL_INTERACTION_TYPE,
            invalidPrompt: templateData.invalidPrompt || '',
            timeoutPrompt: templateData.timeoutPrompt || '',
            usedBy: templateData.usedBy || 'CLINIC',
            mediumType: templateData.mediumType || 'CALL',
        });
        setOptionRows(rows);
        setOriginalOptionRows(rows.map((row) => ({ ...row })));
    };

    useEffect(() => {
        const fetchTemplate = async () => {
            try {
                setLoading(true);
                setError(null);

                if (!id || id === 'undefined') {
                    throw new Error('Invalid template ID');
                }

                const response = await templateService.getEcxTemplateById(id);
                if (!response?.data) {
                    throw new Error('No template data received');
                }

                const templateData = response.data.data || response.data;
                setOriginalData(templateData);
                applyTemplateToForm(templateData);
            } catch (err) {
                console.error('Error fetching template:', err);
                setError(err.message);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: err.message || 'Failed to load template. Please try again.',
                    confirmButtonColor: '#dc3545'
                }).then(() => navigate('/admin-templates'));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchTemplate();
        } else {
            setLoading(false);
            setError('No template ID provided');
        }
    }, [id, navigate]);

    const hasChanges = useMemo(() => {
        if (!originalData) return false;
        return (
            formData.content !== (originalData.content || '') ||
            formData.voice !== (originalData.voice || DEFAULT_CALL_VOICE) ||
            formData.language !== (originalData.language || DEFAULT_CALL_LANGUAGE) ||
            formData.interactionType !== (originalData.interactionType || DEFAULT_CALL_INTERACTION_TYPE) ||
            (formData.invalidPrompt || '') !== (originalData.invalidPrompt || '') ||
            (formData.timeoutPrompt || '') !== (originalData.timeoutPrompt || '') ||
            formData.usedBy !== originalData.usedBy ||
            serializeCallOptions(optionRowsToMap(optionRows)) !==
                serializeCallOptions(optionRowsToMap(originalOptionRows))
        );
    }, [formData, originalData, optionRows, originalOptionRows]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const interactionType = formData.interactionType || DEFAULT_CALL_INTERACTION_TYPE;
        if (interactionType === 'DTMF' && optionRows.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Missing options',
                text: 'DTMF templates need at least one digit option.',
                confirmButtonColor: '#0dcaf0',
            });
            return;
        }
        const digits = optionRows.map((row) => row.digit);
        if (interactionType === 'DTMF' && new Set(digits).size !== digits.length) {
            Swal.fire({
                icon: 'warning',
                title: 'Duplicate digits',
                text: 'Each digit option must be unique.',
                confirmButtonColor: '#0dcaf0',
            });
            return;
        }

        try {
            setSubmitting(true);
            await templateService.updateEcxTemplate(id, {
                reminderName: formData.reminderName,
                mediumType: 'CALL',
                content: formData.content,
                voice: formData.voice,
                language: formData.language,
                interactionType,
                options: interactionType === 'DTMF' ? optionRowsToMap(optionRows) : null,
                invalidPrompt: formData.invalidPrompt || null,
                timeoutPrompt: formData.timeoutPrompt || null,
                usedBy: formData.usedBy,
            });

            await Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Call template updated successfully!',
                confirmButtonColor: '#0dcaf0',
                timer: 2000,
                showConfirmButton: true
            });
            navigate('/admin-templates');
        } catch (err) {
            console.error('Error updating Call template:', err);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text:
                    err.response?.data?.message ||
                    err.response?.data?.error ||
                    err.message ||
                    'Failed to update Call template. Please try again.',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        if (!hasChanges) {
            navigate('/admin-templates');
            return;
        }
        Swal.fire({
            title: 'Unsaved changes',
            text: 'Discard your edits and leave this page?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, discard',
            cancelButtonText: 'Keep editing'
        }).then((result) => {
            if (result.isConfirmed) {
                navigate('/admin-templates');
            }
        });
    };

    const handleReset = () => {
        if (!originalData) return;
        Swal.fire({
            title: 'Reset changes?',
            text: 'Restore all fields to the last saved values.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#0dcaf0',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, reset',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                applyTemplateToForm(originalData);
            }
        });
    };

    if (loading) {
        return (
            <AdminCallTemplatePage
                title="Edit Call Template"
                subtitle="Update master ECX call script, voice, language, and DTMF defaults"
            >
                <Loading />
            </AdminCallTemplatePage>
        );
    }

    if (error || !originalData) {
        return (
            <AdminCallTemplatePage
                title="Edit Call Template"
                subtitle="Update master ECX call script, voice, language, and DTMF defaults"
            >
                <div className="alert alert-danger admin-call-template-alert mb-0">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error || 'Template not found or failed to load.'}
                </div>
            </AdminCallTemplatePage>
        );
    }

    return (
        <AdminCallTemplatePage
            title="Edit Call Template"
            subtitle="Update master ECX call script, voice, language, and DTMF defaults"
            headerBadge={
                <div className="d-flex flex-wrap align-items-center gap-2">
                    {formData.interactionType === 'DTMF' ? (
                        <span className="badge bg-info-subtle text-info border border-info-subtle">
                            DTMF
                        </span>
                    ) : (
                        <span className="badge bg-light text-muted border">Announce only</span>
                    )}
                    {hasChanges ? (
                        <span className="badge bg-warning text-dark">
                            <i className="bi bi-pencil me-1"></i>
                            Unsaved changes
                        </span>
                    ) : (
                        <span className="badge bg-light text-muted border">
                            Saved
                        </span>
                    )}
                </div>
            }
        >
            <form onSubmit={handleSubmit}>
                <AdminCallTemplateMetaGrid
                    items={[
                        { label: 'Editing', value: originalData.reminderName },
                        { label: 'Medium', value: originalData.mediumType || 'CALL' },
                        { label: 'Interaction', value: formData.interactionType || 'NONE' },
                        { label: 'Created', value: originalData.createdAt || '—' },
                        { label: 'Modified', value: originalData.modifiedAt || '—' },
                        { label: 'Template ID', value: originalData.id, mono: true },
                    ]}
                />

                <AdminCallTemplateFields
                    formData={formData}
                    onChange={handleChange}
                    contentField="content"
                    reminderNameControl={
                        <>
                            <input
                                type="text"
                                className="form-control"
                                value={formData.reminderName}
                                readOnly
                                disabled
                            />
                            <div className="form-text">
                                Template name cannot be changed
                            </div>
                        </>
                    }
                />

                <AdminCallTemplateInteractionFields
                    formData={formData}
                    onChange={handleChange}
                    optionRows={optionRows}
                    onOptionRowsChange={setOptionRows}
                />

                <div className="mt-4 pt-3 border-top">
                    <AdminCallTemplateFooter
                        left={
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={handleReset}
                                disabled={!hasChanges || submitting}
                            >
                                <i className="bi bi-arrow-counterclockwise me-1"></i>
                                Reset
                            </button>
                        }
                        onCancel={handleCancel}
                        onSubmitLabel="Update call template"
                        submitting={submitting}
                        submitDisabled={!hasChanges}
                        submitVariant="info"
                    />
                </div>
            </form>
        </AdminCallTemplatePage>
    );
};

export default EditAdminCallTemplate;
